import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Card, CardContent } from "@/components/ui/card";

import {
  useCatalogueQuery,
  useExpertiseQuery,
  useGoalsQuery,
  useRegistrationQuery,
} from "../../api/hooks";
import { ErrorState, Loading } from "../ui/states";
import { GoalsStep } from "./steps/GoalsStep";

/**
 * Part 2 of expert registration — objectives + action paths, unlocked only after
 * CQDD approval. Reached from the dashboard call-to-action (and the approval
 * email link). Completing it the first time publishes immediately server-side;
 * a later edit stages for re-validation. Anyone who is not an approved expert is
 * sent back to their dashboard.
 */
export function ObjectivesFlow({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const registration = useRegistrationQuery();
  const catalogue = useCatalogueQuery();
  const expertise = useExpertiseQuery();
  const goals = useGoalsQuery();

  // Part 2 is only for an approved (validated) expert; anyone else belongs on
  // the dashboard. Redirect once the profile has loaded.
  const notValidated =
    !registration.isPending &&
    !registration.isError &&
    registration.data?.status !== "validated";
  useEffect(() => {
    if (notValidated) onDone();
  }, [notValidated, onDone]);

  const pending =
    registration.isPending ||
    catalogue.isPending ||
    expertise.isPending ||
    goals.isPending;
  if (pending) return <Loading />;
  if (
    registration.isError ||
    !registration.data ||
    !catalogue.data ||
    !expertise.data ||
    !goals.data
  ) {
    return <ErrorState message={t("expert.register.loadError")} />;
  }
  if (notValidated) return <Loading />;

  // An enjeu in a change still waiting for the CQDD offers nothing yet: it is
  // left out, and GoalsStep says why.
  const pendingIssueIds = new Set(goals.data.pending_issue_ids ?? []);
  const selectedIssueIds = new Set(
    expertise.data.expertises
      .map((e) => e.issue)
      .filter((issue) => !pendingIssueIds.has(issue)),
  );

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <h2 className="text-xl font-semibold">{t("expert.objectives.heading")}</h2>
        <p className="text-sm text-muted-foreground">{t("expert.objectives.help")}</p>
      </header>
      <Card>
        <CardContent className="pt-6">
          <GoalsStep
            catalogue={catalogue.data.thematics}
            selectedIssueIds={selectedIssueIds}
            goalsState={goals.data}
            onSaved={onDone}
            onBack={onDone}
          />
        </CardContent>
      </Card>
    </div>
  );
}
